import { html, LitElement, nothing, type PropertyValueMap, unsafeCSS } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import type { Banner, CtaAction } from '../../../app/package/base/cms/package/cms-domain';
import { TabNavigationType } from '../../../app/package/base/router/router-types/router-types';
import { TrackableEventAction, TrackingEventSource } from '../../../app/package/base/service/tracking/tracking-service';
import { STORAGE_KEY_IS_VIDEO_PAUSED } from '../../../constants';
import { FetchPriority, ImageDecoding, ImageLoading } from '../../base/package/api/package/gaming/gaming-domain';
import { I18n } from '../../base/package/services/package/i18n/i18n.de';
import { dispatchCustomEvent } from '../../base/package/util/event';
import { rasterizeToFirstFrame } from '../../base/package/util/game-asset-url';
import '../ui-btn/ui-btn';
import '../ui-icons/ui-play-pause-icon';
import { localStorage_getOrNull, localStorage_set } from '../../base/package/util/localStorage';
import { reduceMotionStore } from '../../base/package/util/reduce-motion';
import styles from './ui-banner.css?inline';

const CName = 'ui-banner';

/**
 * @fires banner-video-paused - Fired when the banner video is paused via user interaction
 * @fires draw-video-canvas - Fired when the banner video starts playing, detail contains the HTMLVideoElement
 * @fires src-loaded - Fired when the banner media has loaded
 * @fires update-animation-speed - Fired when the banner video starts playing, detail contains the video duration
 * @fires video-ended - Fired when the banner video has ended
 */
@customElement(CName)
export class UiBanner extends LitElement {
  static readonly styles = unsafeCSS(styles);

  @property({ type: Object }) banner: Banner;
  @property({ type: String }) class = '';
  @property({ type: Boolean }) loggedIn?: boolean;
  @property({ type: Boolean }) isDesktop = false;
  @property({ type: Boolean }) isSingleSlide = false;
  @property({ type: Boolean }) isActive = false;
  @property({ type: Boolean }) isNext = false;
  @property({ type: Boolean }) isSmall = false;
  @property({ type: Boolean }) isSvg = false;
  @property({ type: Boolean }) isInitial = false;
  @property({ type: Number }) position: number;

  @state() _isResuming = false;
  @state() _isVideoPlaying = false;
  @state() _shouldRenderImage = true;
  @state() private _staticImgSrc?: string;

  @query('#banner') private readonly _bannerElement?: HTMLVideoElement;

  private _reduceMotionCallback?: (v: boolean) => void;

  private readonly _onVideoLoadedData = () => {
    if (this._bannerElement) {
      this._bannerElement.muted = true; // Set video to mute to avoid autoplay issues in Firefox
    }
    this._playVideo();
  };

  willUpdate(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    if (_changedProperties.has('isActive')) {
      if (this.isActive) {
        // Detach and reattach SVG images to restart their animations.
        // Bitmap images don't need this — skip to avoid unnecessary DOM churn.
        // When reduce-motion is active, skip the restart entirely — the SVG
        // is rendered as a static raster frame instead.
        if (this._isSvgImage && !reduceMotionStore.value) {
          this._shouldRenderImage = false;
          requestAnimationFrame(() => {
            this._shouldRenderImage = true;
          });
        }

        if (this._isSvgImage && reduceMotionStore.value) {
          this._rasterizeSvgImage();
        }

        this.dispatchEvent(
          new CustomEvent<boolean>('slide-type-updated', {
            detail: Boolean(this.banner.desktop_vid || this.banner.mobile_vid),
          }),
        );
      }
    }
  }

  // zombie render cycles on detached elements.
  protected override scheduleUpdate(): undefined | Promise<unknown> {
    if (!this.isConnected) return;
    return super.scheduleUpdate() as undefined | Promise<unknown>;
  }

  connectedCallback(): void {
    super.connectedCallback();
    this._reduceMotionCallback = reduceMotionStore.subscribe(() => {
      if (reduceMotionStore.value && this._isSvgImage) {
        this._rasterizeSvgImage();
      } else if (!reduceMotionStore.value) {
        // Restore animated SVG when reduce-motion is disabled
        this._staticImgSrc = undefined;
      }
    });
  }

  // Lit does not guard performUpdate with isConnected — prevent

  disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this._reduceMotionCallback) {
      reduceMotionStore.unsubscribe(this._reduceMotionCallback);
    }
    this._bannerElement?.removeEventListener('loadeddata', this._onVideoLoadedData);
  }

  protected firstUpdated(): void {
    if (this._bannerElement && this._hasVideo && this.isInitial && this.isActive) {
      this._bannerElement.addEventListener('loadeddata', this._onVideoLoadedData);
    }
  }

  updated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    if (!_changedProperties.has('isActive') || !this._hasVideo) {
      return;
    }

    if (this.isActive) {
      this._playVideo();
    } else {
      this._stopVideo();
    }
  }

  render() {
    const classList = classMap({
      banner: true,
      'banner-logged-in': this.loggedIn === true && !this.isDesktop,
      desktop: this.isDesktop,
    });

    return html`
      <div class="${classList}">
        ${this._renderBanner()}
        <div class="content">
          <div class="content__title">${this.banner.title ?? ''}</div>
          <p class="content__subtitle">${this.banner.subtitle ?? ''}</p>
          ${this._renderAction()} ${this._renderSubAction()}
        </div>
        <div class="overlay"></div>
      </div>
    `;
  }

  private _renderBanner() {
    if (this.isDesktop) {
      return this._renderDesktopBanner();
    }

    return this._renderMobileBanner();
  }

  private _renderDesktopBanner() {
    if (this.isSmall) {
      return this._renderImage(this.banner.mobile_image_small, this.banner.mobile_image_small_type);
    }

    return this.banner.desktop_vid
      ? this._renderVideo(this.banner.desktop_vid, this.banner.desktop_vid_h265, this.banner.desktop_image)
      : this._renderImage(this.banner.desktop_image, this.banner.desktop_image_type);
  }

  private _renderMobileBanner() {
    if (this.loggedIn) {
      if (this.banner.mobile_vid_small) {
        return this._renderVideo(
          this.banner.mobile_vid_small,
          this.banner.mobile_vid_small_h265,
          this.banner.mobile_image_small,
        );
      }

      return this._renderImage(this.banner.mobile_image_small, this.banner.mobile_image_small_type);
    }

    if (this.banner.mobile_vid) {
      return this._renderVideo(this.banner.mobile_vid, this.banner.mobile_vid_h265, this.banner.mobile_image);
    }

    return this._renderImage(this.banner.mobile_image, this.banner.mobile_image_type);
  }

  private _renderImage(src: string, type: string) {
    if (!this._shouldRenderImage) {
      return nothing;
    }

    const priority = this.position === 0 ? FetchPriority.HIGH : FetchPriority.LOW;
    const loading = priority === FetchPriority.HIGH ? ImageLoading.EAGER : ImageLoading.LAZY;
    const decoding = priority === FetchPriority.HIGH ? ImageDecoding.SYNC : ImageDecoding.ASYNC;

    if (type === 'image/svg+xml') {
      // When reduce-motion is active, render a static raster frame
      // instead of the animated <object> tag.
      if (reduceMotionStore.value && this._staticImgSrc) {
        return html`<div class="image">
          <img src=${this._staticImgSrc}
            @load=${this._onLoad}
            alt=${this.banner.title}
            decoding=${decoding}
            fetchpriority=${priority}
            loading=${loading}
          />
        </div>`;
      }

      return html`<div class="image">
        <object
          @load=${this._onLoad}
          type="image/svg+xml"
          data=${src}
          >
            <img src=${src}
              @load=${this._onLoad}
              alt=${this.banner.title}
              crossorigin="anonymous"
              decoding=${decoding}
              fetchpriority=${priority}
              loading=${loading}
            />
        </object>
      </div>`;
    }

    return html`
      <div class="image">
        <img src=${src}
          @load=${this._onLoad}
          alt=${this.banner.title}
          crossorigin="anonymous"
          decoding=${decoding}
          fetchpriority=${priority}
          loading=${loading}
        />
      </div>
    `;
  }

  private _renderVideo(h264VideoSrc: string, h265VideoSrc?: string, posterSrc?: string) {
    const shouldLoadSources = this.isActive || this.isNext;
    const priority = this.position === 0 ? FetchPriority.HIGH : FetchPriority.LOW;
    // Active slide: full download. Next slide: metadata only (dimensions + first frames).
    // All others: no sources injected, preload irrelevant.
    const preload = this.isActive ? 'auto' : 'metadata';
    return html`
      <div class="video-mask">
        <video
          id="banner"
          muted
          playsinline
          .loop=${this.isSingleSlide}
          crossorigin="anonymous"
          disablePictureInPicture
          disableRemotePlayback
          fetchpriority=${priority}
          preload=${preload}
          poster=${posterSrc ?? nothing}
          aria-label=${window.$app.$t.get(I18n.a11y.video.lobbyBanner)}
          @ended=${this._videoEnded}
          @loadeddata=${this._onLoad}
          @play=${this._drawVideoCanvas}
        >
          ${
            shouldLoadSources &&
            // Important:
            // As browsers are picky about details, we specify multiple variants.
            // Codecs must be specified wihtout use of quotes.
            // Firefox fails on Windows if any codecs value is set.
            html`
              ${
                h265VideoSrc
                  ? html`
                  <source src=${h265VideoSrc} type='video/mp4; codecs=hvc1' />
                  <source src=${h265VideoSrc} type='video/mp4; codecs=hev1' />
                  <source src=${h265VideoSrc} type='video/mp4; codecs=hvc1.1.6.L93.B0' />
                `
                  : nothing
              }
              <source src=${h264VideoSrc} type='video/mp4; codecs=avc1' />
              <source src=${h264VideoSrc} type='video/mp4' />
            `
          }
          <p lang=${window.$app.appConfig.lang}>
            ${window.$app.$t.get(I18n.a11y.video.lobbyBanner)}
          </p>
        </video>
        <button
          class="video-controls"
          @click=${this._handlePlayPauseClick}
          aria-label=${window.$app.$t.get(this._isVideoPlaying ? I18n.a11y.button.pauseVideo : I18n.a11y.button.playVideo)}
        >
          <ui-play-pause-icon class="icon" .shouldDisplayPauseIcon=${this._isVideoPlaying}></ui-play-pause-icon>
        </button>
      </div>
    `;
  }

  private _renderAction() {
    if (this.banner.action === undefined) {
      return nothing;
    }

    return html`
      <ui-btn variant="accent" @click=${() => this._handleAction(this.banner.action!)}>
        ${this.banner.cta_label ?? ''}
      </ui-btn>
    `;
  }

  private _renderSubAction() {
    if (!this.banner.subaction?.actionType || !this.banner.subaction_description) {
      return nothing;
    }

    return html`
      <span
        @click=${() => this._handleAction(this.banner.subaction!)}
        class="content__subaction ${this.loggedIn ? 'logged-in' : ''}"
      >
        ${this.banner.subaction_description}
      </span>
    `;
  }

  private _handlePlayPauseClick() {
    if (this._isVideoPlaying) {
      localStorage_set(STORAGE_KEY_IS_VIDEO_PAUSED, true);
      this._stopVideo();
      dispatchCustomEvent(this, 'banner-video-paused');
    } else {
      localStorage_set(STORAGE_KEY_IS_VIDEO_PAUSED, false);
      this._resumeVideo();
    }
  }

  /**
   * Absolute URLs of banners should always open in the same tab.
   * Banners will never open a page in a new tab.
   */
  private _handleAction(action: CtaAction) {
    // action.path = '/challenges';
    if (action.actionType === 'launchGame' && action.gameId) {
      this._trackAction(TrackableEventAction.GAME_SELECTED, action.url);
      window.$app.handlePlayNow(this.loggedIn === true, action.gameId);
      return;
    }
    if (action.actionType === 'navigateTo' && action.path) {
      this._trackAction(TrackableEventAction.NAVIGATE_TO, action.url);
      window.$app.router.navigateTo(action.path, TabNavigationType.SAME);
      return;
    }
    if (action.url) {
      window.$app.track.registerCTA(TrackingEventSource.LOBBY_BANNER);
      this._trackAction(this._toPascalCase(this.banner.cta_label), action.url);
      window.$app.router.navigateTo(action.url, TabNavigationType.SAME);
    }
  }

  private get _hasVideo(): boolean {
    if (this.isDesktop) return Boolean(this.banner.desktop_vid);
    if (this.loggedIn) return Boolean(this.banner.mobile_vid_small || this.banner.mobile_vid);
    return Boolean(this.banner.mobile_vid);
  }

  private get _isSvgImage(): boolean {
    if (this.isDesktop) return this.banner.desktop_image_type === 'image/svg+xml';
    if (this.loggedIn) return this.banner.mobile_image_small_type === 'image/svg+xml';
    return this.banner.mobile_image_type === 'image/svg+xml';
  }

  private get _svgImageSrc(): string | undefined {
    if (this.isDesktop) {
      return this.isSmall ? this.banner.mobile_image_small : this.banner.desktop_image;
    }
    if (this.loggedIn) return this.banner.mobile_image_small;
    return this.banner.mobile_image;
  }

  private _rasterizeSvgImage() {
    const src = this._svgImageSrc;
    if (!src) return;
    rasterizeToFirstFrame(src).then((result) => {
      if (!this.isConnected) return;
      if (result !== src) {
        this._staticImgSrc = result;
      }
    });
  }

  private _playVideo() {
    if (!this._bannerElement) {
      return;
    }

    // Update animation speed for slider controls if video has a valid duration
    if (this._bannerElement.duration && !Number.isNaN(this._bannerElement.duration)) {
      this.dispatchEvent(
        new CustomEvent<number>('update-animation-speed', {
          detail: this._bannerElement.duration + 0.5,
        }),
      );
    }

    if (localStorage_getOrNull(STORAGE_KEY_IS_VIDEO_PAUSED)) {
      // Force Safari to render the video element and pause it
      this._bannerElement
        .play()
        .then(() => {
          this._bannerElement!.pause();
        })
        .catch((err) => window.$app.logger.log('could not play and pause the video', err));
      return;
    }

    if (!this._isVideoPlaying) {
      this._bannerElement.currentTime = 0;
      this._bannerElement
        .play()
        .then(() => {
          // TODO: This schedules an update after an update completed, causing a new update to be scheduled. This is inefficient and should be avoided.
          this._isVideoPlaying = true;
        })
        .catch((err) => window.$app.logger.log('ui-banner is undefined', err));
    }
  }

  private _drawVideoCanvas() {
    if (this._isResuming) {
      this._isResuming = false;
      return;
    }

    (this._bannerElement as HTMLVideoElement).currentTime = 0;

    this.dispatchEvent(
      new CustomEvent<HTMLVideoElement>('draw-video-canvas', {
        detail: this._bannerElement,
        bubbles: true,
        composed: true,
      }),
    );
  }

  private _stopVideo() {
    if (this._isVideoPlaying && this._bannerElement) {
      this._bannerElement.pause();
      this._isVideoPlaying = false;
    }
  }

  private _resumeVideo() {
    if (!this._isVideoPlaying && this._bannerElement) {
      this._isResuming = true;
      this._bannerElement.play().catch(() => {
        // Autoplay blocked (Low Power Mode, user preference, or policy restriction)
        this._isVideoPlaying = false;
        this._isResuming = false;
      });
      this._isVideoPlaying = true;
    }
  }

  private _onLoad() {
    if (this.isInitial) {
      this.dispatchEvent(new CustomEvent<string>('src-loaded'));
    }
  }

  private _videoEnded() {
    if (this.isActive) {
      this.dispatchEvent(new CustomEvent<string>('video-ended'));
    }
  }

  private _trackAction(eventAction: TrackableEventAction | string, url?: string) {
    window.$app.track.carouselBanner({
      eventAction,
      promoName: this.banner.title ?? '',
      promoPosition: this.position + 1,
      url,
    });
  }

  private _toPascalCase(str: string): string {
    return str
      .replace(/\w\S*/g, (word) => word.charAt(0).toUpperCase() + word.substring(1).toLowerCase()) // NOSONAR
      .replace(/\s+/g, ''); // NOSONAR
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CName]: UiBanner;
  }
}
